import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, Package, MapPin, Plus, Check, RefreshCw, X, Keyboard, Search, History, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const InwardingPage = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState('manual'); // 'manual' or 'scanner'
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Product lookup
  const [skuInput, setSkuInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [product, setProduct] = useState(null);
  
  // Inwarding form
  const [quantity, setQuantity] = useState(1);
  const [selectedRack, setSelectedRack] = useState('');
  const [notes, setNotes] = useState('');
  const [inwardLoading, setInwardLoading] = useState(false);
  
  // Recent inwards
  const [recentInwards, setRecentInwards] = useState([]);
  
  // Barcode scanner
  const videoRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scannerError, setScannerError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      loadRacks(selectedLocation);
    }
  }, [selectedLocation]);

  const loadInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Try Shopify locations first, fallback to enterprise/locations (from DB)
      let locs = [];
      try {
        const locationsRes = await axios.get(`${API_BASE_URL}/shopify/locations`, { headers });
        locs = locationsRes.data.locations || [];
      } catch (shopifyError) {
        console.log('Shopify locations failed, falling back to enterprise locations');
        const locationsRes = await axios.get(`${API_BASE_URL}/enterprise/locations`, { headers });
        locs = locationsRes.data.locations || [];
      }
      
      setLocations(locs);
      
      // Auto-select first location - ensure it's a string
      if (locs.length > 0 && locs[0]) {
        setSelectedLocation(String(locs[0].id || locs[0].shopify_location_id || ''));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const loadRacks = async (locationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token for loading racks');
        setRacks([]);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/warehouse/racks?location_id=${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRacks(response.data?.racks || []);
    } catch (error) {
      console.error('Failed to load racks:', error);
      setRacks([]); // Reset racks on error to prevent undefined issues
    }
  };

  const handleLookup = async (sku = skuInput) => {
    if (!sku.trim()) {
      toast.error('Please enter a SKU or barcode');
      return;
    }

    setLookupLoading(true);
    setProduct(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/inventory/lookup/${encodeURIComponent(sku.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setProduct(response.data);
        // Default to the SKU's existing rack if known; otherwise "Default"
        // so the inwarded stock is never parked in a phantom rack-null
        // bucket (the bug that drove May-2026 stock-drift reports).
        setSelectedRack(response.data.rack_number || 'Default');
        
        // Vibrate on success (mobile)
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
        
        toast.success(`Found: ${response.data.name || 'Product'}`);
      } else {
        toast.error('Product not found');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      // Vibrate longer on error (mobile)
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Product not found');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleInward = async () => {
    if (!product) {
      toast.error('Please lookup a product first');
      return;
    }
    if (!selectedLocation) {
      toast.error('Please select a location');
      return;
    }
    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    setInwardLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/inventory/inward`,
        {
          sku: product.sku,
          quantity: quantity,
          location_id: selectedLocation,
          rack_number: selectedRack && selectedRack !== 'none' ? selectedRack : 'Default',
          notes: notes || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-User-Id': user?.id || 'unknown',
            'X-User-Name': user?.full_name || user?.email || 'Unknown'
          }
        }
      );
      
      // Success feedback
      if (navigator.vibrate) {
        navigator.vibrate([50, 30, 50, 30, 50]);
      }
      
      // Show success message with Shopify sync status
      const data = response.data;
      if (data.shopify_synced) {
        toast.success(`Inwarded ${quantity} x ${product.sku} (Synced to Shopify)`);
      } else {
        toast.success(`Inwarded ${quantity} x ${product.sku}`);
        if (data.shopify_sync_error) {
          toast.warning(`Note: Shopify sync pending - ${data.shopify_sync_error.substring(0, 100)}`);
        }
      }
      
      // Add to recent inwards
      setRecentInwards(prev => [{
        sku: product.sku,
        name: product.name,
        quantity: quantity,
        location: locations.find(l => String(l.id) === String(selectedLocation))?.name || 'Unknown',
        timestamp: new Date().toISOString(),
        shopify_synced: data.shopify_synced
      }, ...prev.slice(0, 9)]);
      
      // Reset form
      setProduct(null);
      setSkuInput('');
      setQuantity(1);
      setNotes('');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to inward stock');
    } finally {
      setInwardLoading(false);
    }
  };

  const startScanner = async () => {
    setScannerError(null);
    setScanning(true);
    
    try {
      // Dynamic import of html5-qrcode
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const html5QrCode = new Html5Qrcode("qr-reader");
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 150 }
        },
        (decodedText) => {
          // Success - stop scanner and lookup
          html5QrCode.stop();
          setScanning(false);
          setSkuInput(decodedText);
          handleLookup(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (no QR found in frame)
        }
      );
    } catch (error) {
      console.error('Scanner error:', error);
      setScannerError('Camera access denied or not available. Please use manual entry.');
      setScanning(false);
    }
  };

  const stopScanner = () => {
    setScanning(false);
    // The scanner will be stopped when component unmounts or we try to start again
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-wt-bg">
        <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg">
      {/* Mobile-optimized header */}
      <div className="bg-wt-paper border-b border-wt-border p-4 sticky top-0 z-10">
        <h1 className="font-playfair text-2xl font-bold text-wt-text-primary text-center">
          Inventory Inwarding
        </h1>
        <p className="text-wt-text-muted text-center text-sm">Scan or enter SKU to add stock</p>
      </div>

      <div className="p-4 max-w-lg mx-auto space-y-4">
        {/* Location Selection */}
        <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
          <Label className="text-wt-text-primary flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-wt-gold" />
            Warehouse Location
          </Label>
          <Select value={String(selectedLocation || '')} onValueChange={(val) => setSelectedLocation(val)}>
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border">
              {locations && locations.map(loc => (
                <SelectItem 
                  key={String(loc.id || loc.shopify_location_id || Math.random())} 
                  value={String(loc.id || loc.shopify_location_id || '')} 
                  className="text-wt-text-primary"
                >
                  {loc.name || 'Unknown Location'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2">
          <Button
            onClick={() => setMode('manual')}
            className={mode === 'manual' 
              ? 'flex-1 bg-wt-gold text-black' 
              : 'flex-1 bg-wt-subtle border border-wt-border text-wt-text-primary hover:bg-wt-paper'
            }
          >
            <Keyboard className="h-4 w-4 mr-2" />
            Manual
          </Button>
          <Button
            onClick={() => {
              setMode('scanner');
              startScanner();
            }}
            className={mode === 'scanner' 
              ? 'flex-1 bg-wt-gold text-black' 
              : 'flex-1 bg-wt-subtle border border-wt-border text-wt-text-primary hover:bg-wt-paper'
            }
          >
            <Camera className="h-4 w-4 mr-2" />
            Scanner
          </Button>
        </div>

        {/* Scanner View */}
        {mode === 'scanner' && (
          <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
            {scanning ? (
              <div className="space-y-4">
                <div id="qr-reader" className="w-full rounded-lg overflow-hidden"></div>
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="w-full border-red-500/50 text-red-400"
                >
                  <X className="h-4 w-4 mr-2" />
                  Stop Scanner
                </Button>
              </div>
            ) : scannerError ? (
              <div className="text-center py-8">
                <Camera className="h-12 w-12 mx-auto mb-4 text-red-400" />
                <p className="text-red-400 text-sm mb-4">{scannerError}</p>
                <Button onClick={startScanner} className="bg-wt-gold text-black">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <ScanLine className="h-12 w-12 mx-auto mb-4 text-wt-gold" />
                <p className="text-wt-text-muted mb-4">Camera ready</p>
                <Button onClick={startScanner} className="bg-wt-gold text-black">
                  <Camera className="h-4 w-4 mr-2" />
                  Start Scanning
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Manual Entry */}
        {mode === 'manual' && (
          <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
            <Label className="text-wt-text-primary mb-2 block">SKU or Barcode</Label>
            <div className="flex gap-2">
              <Input
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                placeholder="Enter SKU or scan barcode"
                className="bg-wt-bg border-wt-border text-wt-text-primary font-mono flex-1"
                autoFocus
              />
              <Button 
                onClick={() => handleLookup()}
                disabled={lookupLoading}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {lookupLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Product Found */}
        {product && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-paper border border-green-500/30 rounded-xl p-4 space-y-4"
          >
            {/* Product Info */}
            <div className="flex gap-4">
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name || 'Product'}
                  className="w-20 h-20 object-cover rounded-lg border border-wt-border"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-20 h-20 bg-wt-subtle rounded-lg flex items-center justify-center">
                  <Package className="h-8 w-8 text-wt-text-muted" />
                </div>
              )}
              <div className="flex-1">
                <p className="text-wt-text-primary font-medium line-clamp-2">{product.name || 'Unknown Product'}</p>
                {product.variant_title && (
                  <p className="text-sm text-wt-text-muted">{product.variant_title}</p>
                )}
                <p className="text-sm font-mono text-wt-gold mt-1">{product.sku || 'N/A'}</p>
                <p className="text-xs text-wt-text-muted">
                  Current Stock: {
                    // Show location-specific stock if available - handle type coercion safely
                    (() => {
                      try {
                        if (!product.locations || !Array.isArray(product.locations) || product.locations.length === 0) {
                          return 0;
                        }
                        const locationMatch = product.locations.find(l => {
                          if (!l) return false;
                          return String(l.location_id || '') === String(selectedLocation || '') || 
                                 String(l.shopify_location_id || '') === String(selectedLocation || '');
                        });
                        return locationMatch?.available ?? locationMatch?.quantity ?? 0;
                      } catch (e) {
                        console.error('Stock calculation error:', e);
                        return 0;
                      }
                    })()
                  }
                </p>
              </div>
            </div>

            {/* Quantity */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Quantity to Add</Label>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  variant="outline"
                  className="border-wt-border text-wt-text-primary h-12 w-12 text-xl"
                >
                  -
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-center text-2xl font-bold h-12 w-24"
                  min="1"
                />
                <Button
                  onClick={() => setQuantity(quantity + 1)}
                  variant="outline"
                  className="border-wt-border text-wt-text-primary h-12 w-12 text-xl"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Rack Selection */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Rack Location</Label>
              <Select value={selectedRack || 'Default'} onValueChange={setSelectedRack}>
                <SelectTrigger
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="inward-rack-select"
                >
                  <SelectValue placeholder="Select rack" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border max-h-48">
                  {/* "Default" is always present so the operator can still
                      receive stock against a SKU that has no assigned
                      rack. Previously this list included a "No rack
                      assigned" option whose value was null — that drove
                      the May-2026 phantom-rack drift where inwarded stock
                      vanished from the per-rack view. */}
                  <SelectItem
                    value="Default"
                    className="text-wt-text-primary font-mono"
                    data-testid="inward-rack-option-default"
                  >
                    Default
                  </SelectItem>
                  {(racks || []).map(rack => (
                    <SelectItem
                      key={rack.id}
                      value={rack.rack_number || rack.id}
                      className="text-wt-text-primary font-mono"
                    >
                      {rack.rack_number || rack.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-wt-text-muted mt-1">
                Stock must land in a real rack. "Default" is the generic
                bucket — never leave this blank.
              </p>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Notes (Optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., GRN #123, Supplier delivery"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            {/* Inward Button */}
            <Button
              onClick={handleInward}
              disabled={inwardLoading}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg"
              data-testid="inward-btn"
            >
              {inwardLoading ? (
                <RefreshCw className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2" />
                  Add {quantity} to Stock
                </>
              )}
            </Button>

            {/* Clear */}
            <Button
              onClick={() => {
                setProduct(null);
                setSkuInput('');
                setQuantity(1);
              }}
              variant="ghost"
              className="w-full text-wt-text-muted"
            >
              <X className="h-4 w-4 mr-2" />
              Clear & Scan Next
            </Button>
          </motion.div>
        )}

        {/* Recent Inwards */}
        {recentInwards.length > 0 && (
          <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
            <h3 className="text-wt-text-primary font-medium mb-3 flex items-center gap-2">
              <History className="h-4 w-4 text-wt-gold" />
              Recent Inwards
            </h3>
            <div className="space-y-2">
              {recentInwards.map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between py-2 border-b border-wt-border/50 last:border-0"
                >
                  <div>
                    <p className="text-sm text-wt-text-primary">{item.name}</p>
                    <p className="text-xs text-wt-text-muted font-mono">{item.sku}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Badge className="bg-green-500/20 text-green-400 border-0">
                        +{item.quantity}
                      </Badge>
                      {item.shopify_synced && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                          Synced
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-wt-text-muted mt-1">{item.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InwardingPage;
